import { DiscreteToLn2EntryInterface } from "../../../interfaces/Adapter.interface";

/**
 * Pin-assignment checks for the bank editor (LIFT-393).
 *
 * A Sequent/Numato adapter's `discrete_2_message` array is a single shared
 * template spanning every bank the adapter serves. Each entry carries the
 * bank_id + lift_id the user assigned to that pin; entries with neither belong
 * to no bank at all.
 *
 * LiftCloud's CDO generation treats those two states differently, and this
 * module mirrors that split exactly so the wizard never lets an operator save a
 * configuration the backend will refuse:
 *
 *   - A bank with **no** assigned pins is a hard error at CDO build time
 *     ("no pin entries assigned to bank ... assign pins ... before generating
 *     the CDO"). The wizard blocks the save instead of letting it fail later at
 *     the edge — see `bankHasNoAssignedPins`.
 *   - Pins assigned to **no** bank are dropped with a warning at CDO build
 *     time. They are a legitimate intermediate state (a pin defined on the
 *     adapter but not yet used), so the wizard warns without blocking — see
 *     `countUnassignedPins`.
 *
 * The distinction matters: the original LIFT-393 symptom was a CDO that reached
 * VTS looking structurally valid while carrying zero-UUID bank/lift ids, so
 * simulator traffic could not be attributed to any bank or lift. Both checks
 * exist to make that state unreachable from the wizard rather than merely
 * diagnosable after the fact.
 */

/** Minimal shape of a lift for resolvability checks — the bank's real roster. */
export interface LiftRef {
  id: string;
}

/**
 * An entry is assigned only when it names both a bank and a lift and — when a
 * lift roster is supplied — that lift is one the bank actually has.
 *
 * Exported so every consumer shares one definition. LiftCloud drops a pin
 * carrying either a zero bank id or a zero lift id, so "has a bank but no lift"
 * is not a partially-assigned state — it is unassigned, and any caller
 * re-deriving that inline can drift from what the backend actually does.
 *
 * The roster argument closes a retrofit hole. Naming a lift is not the same as
 * naming a lift that exists: pins written before assignments were bank-scoped
 * carry a synthesized index (`"0"`, `"1"`) rather than a UUID, and a pin can
 * outlive the car it points at. Both satisfy a presence-only check while
 * LiftCloud collapses them to the zero UUID and drops the pin — the LIFT-393
 * symptom this module exists to make unreachable from the wizard.
 *
 * The roster stays optional so callers that genuinely do not have one — it is
 * still being fetched, or the question really is "did anyone fill this in" —
 * keep the presence-only meaning instead of reporting every pin unassigned
 * while the request is in flight.
 */
export function isAssigned(
  entry: DiscreteToLn2EntryInterface,
  lifts?: readonly LiftRef[],
): boolean {
  const liftId = entry?.value?.lift_id?.id;
  if (!entry?.value?.bank_id?.id || !liftId) return false;
  if (!lifts) return true;
  return lifts.some((l) => l.id === liftId);
}

/** True when the entry is assigned and belongs to `bankId`. */
export function isAssignedToBank(
  entry: DiscreteToLn2EntryInterface,
  bankId: string,
  lifts?: readonly LiftRef[],
): boolean {
  return isAssigned(entry, lifts) && entry.value.bank_id?.id === bankId;
}

/**
 * Pins that claim `bankId` but name a lift the bank does not have.
 *
 * Deliberately separate from the save gate. These pins are real configuration
 * an operator authored, not blanks, so a retrofit surfaces them as a warning to
 * repair rather than a wall: blocking the save would strand every estate
 * carrying legacy ids until someone reassigned every pin, including operators
 * who opened the bank to change something unrelated.
 */
export function countUnresolvableLiftPins(
  entries: readonly DiscreteToLn2EntryInterface[],
  bankId: string,
  lifts: readonly LiftRef[],
): number {
  if (!bankId) return 0;
  return entries.filter(
    (e) =>
      e.value.bank_id?.id === bankId &&
      Boolean(e.value.lift_id?.id) &&
      !lifts.some((l) => l.id === e.value.lift_id?.id),
  ).length;
}

/**
 * Number of pins assigned to `bankId`. This is the count LiftCloud will narrow
 * the CDO down to for this bank.
 */
export function countPinsAssignedToBank(
  entries: readonly DiscreteToLn2EntryInterface[],
  bankId: string,
): number {
  if (!bankId) return 0;
  return entries.filter((e) => isAssigned(e) && e.value.bank_id?.id === bankId).length;
}

/**
 * Number of pins that name no bank or no lift. LiftCloud drops these from every
 * bank's CDO with a warning naming board + pin.
 */
export function countUnassignedPins(entries: readonly DiscreteToLn2EntryInterface[]): number {
  return entries.filter((e) => !isAssigned(e)).length;
}

/**
 * True when saving would produce a bank whose CDO cannot be generated: the
 * adapter defines pins, but none of them are assigned to this bank.
 *
 * Returns false when the adapter defines no pins at all — that is an adapter
 * configuration gap rather than a bank one, and the pin matrix already tells the
 * operator to configure the adapter CDO first. Blocking the bank save there
 * would strand them with no way forward from this screen.
 *
 * Deliberately presence-only: it passes no lift roster down, so a pin naming a
 * lift the bank no longer has still counts as assigned here and the save stays
 * open. `countUnresolvableLiftPins` is what surfaces those, as a warning.
 */
export function bankHasNoAssignedPins(
  entries: readonly DiscreteToLn2EntryInterface[],
  bankId: string,
): boolean {
  if (entries.length === 0) return false;
  return countPinsAssignedToBank(entries, bankId) === 0;
}
